/**
 * EligibilityFlagsDisplay — 2x3 grid of green/red eligibility flags
 *
 * Rows: Informational, Marketing
 * Cols: SMS, Email, Voice
 */
import { CheckCircle, XCircle } from 'lucide-react';
import { cn } from '@respectly/ui';

interface EligibilityFlags {
  informationalSms: boolean;
  informationalEmail: boolean;
  informationalVoice: boolean;
  marketingSms: boolean;
  marketingEmail: boolean;
  marketingVoice: boolean;
}

interface EligibilityFlagsDisplayProps {
  flags: EligibilityFlags;
  compact?: boolean;
  className?: string;
}

const CHANNELS = ['SMS', 'Email', 'Voice'] as const;
const PURPOSES = ['Informational', 'Marketing'] as const;

function FlagIcon({ eligible, compact }: { eligible: boolean; compact?: boolean }) {
  const size = compact ? 'w-3.5 h-3.5' : 'w-4 h-4';
  if (eligible) {
    return <CheckCircle className={cn(size, 'text-emerald-500 dark:text-emerald-400')} />;
  }
  return <XCircle className={cn(size, 'text-red-500 dark:text-red-400')} />;
}

export function EligibilityFlagsDisplay({
  flags,
  compact,
  className,
}: EligibilityFlagsDisplayProps) {
  const grid: boolean[][] = [
    [flags.informationalSms, flags.informationalEmail, flags.informationalVoice],
    [flags.marketingSms, flags.marketingEmail, flags.marketingVoice],
  ];

  const cellPad = compact ? 'p-1' : 'p-1.5';
  const textSize = compact ? 'text-[10px]' : 'text-xs';

  return (
    <div className={cn('inline-block', className)}>
      <table className="border-collapse">
        <thead>
          <tr>
            <th />
            {CHANNELS.map((ch) => (
              <th
                key={ch}
                className={cn(
                  cellPad,
                  textSize,
                  'font-medium text-slate-500 dark:text-slate-400 text-center'
                )}
              >
                {ch}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {PURPOSES.map((purpose, ri) => (
            <tr key={purpose}>
              <td
                className={cn(
                  cellPad,
                  textSize,
                  'font-medium text-slate-500 dark:text-slate-400 pr-2 text-right'
                )}
              >
                {purpose}
              </td>
              {grid[ri]?.map((eligible, ci) => (
                <td key={ci} className={cn(cellPad, 'text-center')}>
                  <FlagIcon eligible={eligible} compact={compact} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
